import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  askQuestion,
  clearSelectedChart,
  createThread,
  deleteChart,
  fetchCharts,
  fetchThreads,
  generateChart,
  loadMessageData,
  loadThread,
  rateMessage,
  refineChart,
  refreshChartData,
  refreshMessageData,
  regenerateChartType,
  resumePendingOperations,
  saveChart,
  selectChart,
  setMessageChartSpec,
  setSavedChartToTable
} from '../../../actions/aiAnalyticsActions';

import AskProgress from './components/AskProgress';
import ChatHeader from './components/ChatHeader';
import ChatMessage from './components/ChatMessage';
import InstructionsPanel from './components/InstructionsPanel';
import SaveChartDialog from './components/SaveChartDialog';
import SavedChartView from './components/SavedChartView';
import ThreadSidebar from './components/ThreadSidebar';
import { messageIdOf } from './services/aiDataUtils';
import { deriveChartName, messageKeyOf, questionForMessage } from './AiAnalyticsChat.service';
import { AI_COLORS } from './aiAnalyticsConstants';
import useSessionSelectionRestore from './useSessionSelectionRestore';

import './AiAnalytics.scss';

export default function AiAnalyticsChat() {
  const dispatch = useDispatch();
  const { activeThread, threadsLoading, pendingAsks, error, savedCharts, selectedChart } = useSelector(
    state => state.aiAnalytics
  );
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pendingChart, setPendingChart] = useState(null);
  const [savingChart, setSavingChart] = useState(false);

  const activeThreadAsk = activeThread ? pendingAsks[activeThread.id] : null;
  const isAsking = activeThreadAsk != null;
  const busy = threadsLoading || isAsking;

  useEffect(
    function fetchInitialData() {
      dispatch(fetchThreads());
      dispatch(fetchCharts());
      dispatch(resumePendingOperations());
    },
    [dispatch]
  );

  useSessionSelectionRestore({
    activeThreadId: activeThread?.id,
    selectedChartId: selectedChart?.id,
    savedCharts,
    onRestoreThread: threadId => dispatch(loadThread(threadId)),
    onRestoreChart: chart => dispatch(selectChart(chart))
  });

  const messages = activeThread?.messages || [];
  const messageCount = messages.length;
  const lastMessageId = messageCount ? messageIdOf(messages[messageCount - 1]) : null;

  useEffect(
    function scrollToLatestMessage() {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [messageCount, lastMessageId]
  );

  function handleCreateThread() {
    dispatch(createThread());
  }

  async function handleSend() {
    if (!inputValue.trim() || !activeThread || busy) {
      return;
    }
    const question = inputValue.trim();
    setInputValue('');
    dispatch(askQuestion(activeThread.id, question));
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  async function handleGenerateChart(messageId) {
    return dispatch(generateChart(messageId));
  }

  async function handleRefineChart(messageId, instruction) {
    return dispatch(refineChart(messageId, instruction));
  }

  async function handleRefineQuery(instruction, originalMessage) {
    if (!instruction || !activeThread || busy) {
      return;
    }
    const refinedFromMessageId = messageIdOf(originalMessage);
    return dispatch(askQuestion(activeThread.id, instruction, refinedFromMessageId));
  }

  async function handleLoadData(messageId) {
    return dispatch(loadMessageData(messageId));
  }

  async function handleRefreshData(messageId) {
    return dispatch(refreshMessageData(messageId));
  }

  async function handleRefreshChartData(chartId) {
    return dispatch(refreshChartData(chartId));
  }

  function handleSaveChart(messageId, defaultTitle, vegaSpec, sqlQuery) {
    setPendingChart({ messageId, title: deriveChartName(defaultTitle), vegaSpec, sqlQuery });
    setSaveDialogOpen(true);
  }

  async function handleConfirmSave() {
    if (!pendingChart || savingChart) {
      return;
    }
    setSavingChart(true);
    try {
      const saved = await dispatch(
        saveChart({
          messageId: pendingChart.messageId,
          title: pendingChart.title,
          vegaSpec:
            typeof pendingChart.vegaSpec === 'string' ? pendingChart.vegaSpec : JSON.stringify(pendingChart.vegaSpec),
          sqlQuery: pendingChart.sqlQuery
        })
      );
      if (saved) {
        setSaveDialogOpen(false);
        setPendingChart(null);
      }
    } finally {
      setSavingChart(false);
    }
  }

  function handleCancelSave() {
    if (savingChart) {
      return;
    }
    setSaveDialogOpen(false);
  }

  function handleDeleteChart(chartId) {
    dispatch(deleteChart(chartId));
  }

  function handleRegenerateType(chartId, type) {
    return dispatch(regenerateChartType(chartId, type));
  }

  function handleSetTableType(chartId) {
    return dispatch(setSavedChartToTable(chartId));
  }

  function handleSetChartSpec(messageId, spec) {
    return dispatch(setMessageChartSpec(messageId, spec));
  }

  function handleRateMessage(messageId, rating, comment) {
    return dispatch(rateMessage(messageId, rating, comment));
  }

  return (
    <div className="ai-analytics-chat">
      <ThreadSidebar />

      <div className="chat-main">
        {selectedChart ? (
          <SavedChartView
            chart={selectedChart}
            onClose={() => dispatch(clearSelectedChart())}
            onDelete={handleDeleteChart}
            onRegenerateType={handleRegenerateType}
            onSetTableType={handleSetTableType}
            onRefreshData={handleRefreshChartData}
          />
        ) : !activeThread ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" color="text.secondary" gutterBottom>
                AI Analytics
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Ask questions about your data in natural language
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleCreateThread}
                sx={{ color: AI_COLORS.brand, borderColor: AI_COLORS.brand }}
              >
                New Analysis
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <ChatHeader thread={activeThread} />

            <div className="chat-messages">
              {messageCount === 0 && !threadsLoading && (
                <Box sx={{ textAlign: 'center', mt: 6 }}>
                  <Typography variant="h6" color="text.secondary">
                    Ask a question to start a report
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try “Top 5 sites by enrollment in the last 90 days” or “All open reimbursement requests.”
                  </Typography>
                </Box>
              )}
              {messages.map(function(message, index) {
                return (
                  <ChatMessage
                    key={messageKeyOf(message, index)}
                    message={message}
                    question={questionForMessage(messages, index)}
                    threadTitle={activeThread.title}
                    onGenerateChart={handleGenerateChart}
                    onRefineChart={handleRefineChart}
                    onRefineQuery={handleRefineQuery}
                    onSaveChart={handleSaveChart}
                    onLoadData={handleLoadData}
                    onRefreshData={handleRefreshData}
                    onSetChartSpec={handleSetChartSpec}
                    onRateMessage={handleRateMessage}
                  />
                );
              })}
              {isAsking && (
                <AskProgress key="__pending__" phase={activeThreadAsk.phase} reasoning={activeThreadAsk.reasoning} />
              )}
              {error && (
                <Typography variant="body2" color="error" sx={{ ml: 1, mb: 1 }}>
                  {error}
                </Typography>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-area">
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Ask a question"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={busy}
                size="small"
              />
              <IconButton
                onClick={handleSend}
                disabled={!inputValue.trim() || busy}
                sx={{
                  backgroundColor: AI_COLORS.primary,
                  color: AI_COLORS.onBrand,
                  '&:hover': { backgroundColor: AI_COLORS.primaryDark },
                  '&.Mui-disabled': { backgroundColor: AI_COLORS.border }
                }}
              >
                <SendIcon />
              </IconButton>
            </div>
          </>
        )}
      </div>

      <div className="instructions-sidebar">
        <InstructionsPanel />
      </div>

      <SaveChartDialog
        open={saveDialogOpen}
        title={pendingChart?.title || ''}
        saving={savingChart}
        onTitleChange={title => setPendingChart(prev => ({ ...prev, title }))}
        onCancel={handleCancelSave}
        onSave={handleConfirmSave}
      />
    </div>
  );
}
